import { Injectable, EventEmitter } from '@angular/core';

@Injectable()
export class WCAGFocusHandler {
  public skipToBasketClicked: EventEmitter<null> = new EventEmitter();

  private readonly focusableSelector =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';

  storeFocusBeforeRemoval(elementToRemove: HTMLElement): HTMLElement | null {
    if (!elementToRemove || !document.contains(elementToRemove)) {
      return null;
    }
    return this.findNextFocusableElement(elementToRemove);
  }

  restoreFocusAfterRemoval(
    storedElement: HTMLElement | null,
    fallbackSelector?: string,
    delay = 100
  ): void {
    setTimeout(() => {
      if (storedElement && document.contains(storedElement)) {
        storedElement.focus();
      } else if (fallbackSelector) {
        const fallback = document.querySelector(fallbackSelector) as HTMLElement;
        fallback?.focus();
      }
    }, delay);
  }

  private findNextFocusableElement(element: HTMLElement): HTMLElement | null {
    const nextSibling = this.findNextFocusableSibling(element);
    if (nextSibling) {
      return nextSibling;
    }

    const parent = element.parentElement;
    if (!parent) {
      return null;
    }

    const parentNextSibling = this.findNextFocusableSibling(parent);
    if (parentNextSibling) {
      return parentNextSibling;
    }

    const focusableInParent = parent.querySelector(this.focusableSelector) as HTMLElement;
    if (focusableInParent && focusableInParent !== element) {
      return focusableInParent;
    }

    return this.findFocusableInContainer(parent);
  }

  private findNextFocusableSibling(element: HTMLElement): HTMLElement | null {
    let sibling = element.nextElementSibling;
    while (sibling) {
      const htmlSibling = sibling as HTMLElement;
      if (this.isFocusable(htmlSibling)) {
        return htmlSibling;
      }

      const focusableInSibling = sibling.querySelector(this.focusableSelector) as HTMLElement;
      if (focusableInSibling) {
        return focusableInSibling;
      }

      sibling = sibling.nextElementSibling;
    }
    return null;
  }

  private isFocusable(element: HTMLElement): boolean {
    if (!element || element.hasAttribute('disabled') || element.getAttribute('tabindex') === '-1') {
      return false;
    }

    const focusableElements = ['button', 'a', 'input', 'select', 'textarea'];
    const tagName = element.tagName.toLowerCase();

    return focusableElements.includes(tagName) || element.hasAttribute('tabindex');
  }

  private findFocusableInContainer(container: HTMLElement): HTMLElement | null {
    const focusableElements = container.querySelectorAll(
      this.focusableSelector
    ) as NodeListOf<HTMLElement>;
    return focusableElements.length > 0 ? focusableElements[0] : null;
  }
}
